import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CalendarDays, Clock, MapPin, Sparkles, Users, GraduationCap, Building2, Cpu, Handshake, TrendingUp, ArrowRight, Bot, Mic, MessageSquare, Send, Mail, Star } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import LanguageToggle from "../components/LanguageToggle";
import AIChat from "../components/AIChat";
import LicenseVerificationCard from "../components/LicenseVerificationCard";
import OtpInput from "../components/OtpInput";
import { api, captureCampaign } from "../lib/nailApi";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=2000&q=80";
const BENEFIT_ICONS = [Users, Building2, GraduationCap, Cpu, Handshake, TrendingUp];

function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!targetIso) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  if (!targetIso) return null;
  const diff = new Date(targetIso).getTime() - now;
  if (Number.isNaN(diff) || diff <= 0) return { d: 0, h: 0, m: 0, s: 0, done: true };
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
    done: false,
  };
}

export default function NailEventLanding() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { lang, t } = useI18n();
  const [track] = useState(() => captureCampaign(searchParams));

  const [event, setEvent] = useState(null);
  const [aiSettings, setAiSettings] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successState, setSuccessState] = useState(null);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState({ business_name: "", phone: "", owner_name: "", email: "", guest_count: "" });
  // OTP 2-step state
  const [otpStep, setOtpStep] = useState("form"); // "form" | "otp"
  const [otpCode, setOtpCode] = useState("");
  const [otpExpiresIn, setOtpExpiresIn] = useState(0);
  const [otpResendIn, setOtpResendIn] = useState(0);
  const [otpRequesting, setOtpRequesting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ev, s] = await Promise.all([
          api.get(`/nail-events/${slug}/public`),
          api.get(`/ai/settings`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setEvent(ev.data);
        setAiSettings(s.data);
      } catch (e) {
        // Graceful fallback for preview/offline-DB environments: render the
        // page with sensible defaults so the design can still be reviewed.
        // Real DB-backed event data takes over on production deploy.
        if (cancelled) return;
        setEvent({
          name: lang === "en" ? "Vietnamese Nail Association Summit 2026" : "Đêm Tôn Vinh & Phát Triển Ngành Nail Việt 2026",
          slug,
          subtitle: null,
          description: lang === "en"
            ? "An evening for Vietnamese nail salon owners — networking, education on business, taxes, labor law, and modern technology."
            : "Một đêm dành cho chủ tiệm nail Việt — kết nối, đào tạo kinh doanh, thuế, luật lao động và công nghệ hiện đại.",
          event_date: "2026-07-18",
          event_time: "5:30 PM",
          end_time: "10:30 PM",
          starts_at: "2026-07-18T17:30:00-05:00",
          location: "The Westin Galleria, Houston, TX",
          venue_name: "The Westin Galleria",
          address: "5060 W Alabama St, Houston, TX 77056",
          city: "Houston",
          maps_url: "https://maps.google.com/?q=The+Westin+Galleria+Houston",
          hero_image_url: null,
          banner_image_url: null,
          gallery: [],
          primary_color: "#0E5B4F",
          secondary_color: "#C9A14A",
          enable_countdown: true,
          enable_ai_chat: true,
          agenda: [],
          sponsors: [],
          speakers: [],
        });
        setAiSettings({ brand_name: "AsterPOS AI Business Advisor", powered_by: "AsterPOS Corp" });
      }
    })();
    return () => { cancelled = true; };
  }, [slug, lang]);

  const benefits = useMemo(() => t("landing.benefits", []), [lang, t]);
  const statsStrip = useMemo(() => t("landing.stats_strip", []), [lang, t]);
  const agendaDefault = useMemo(() => t("landing.agenda_default", []), [lang, t]);
  const agenda = (event?.agenda && event.agenda.length > 0 ? event.agenda : agendaDefault) || [];
  const countdown = useCountdown(event?.starts_at);

  // Inject per-event Open Graph + Twitter Card metadata. Crawlers that
  // execute JS (LinkedIn, X validator, iMessage in some cases) will pick this
  // up. Non-JS bots get the homepage default + can fall back to the
  // `/api/events/{slug}/share` endpoint exposed by the backend.
  useEffect(() => {
    if (!event) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "https://nailvietclub.com";
    const ogImage = event.hero_image_url || event.banner_image_url || `${origin}/og-image.png`;
    const title = event.name || "Nail Viet Club Event";
    const descParts = [event.subtitle, event.location, event.event_date].filter(Boolean);
    const description = descParts.join(" · ") || (event.description || "").slice(0, 180) || "Nail Viet Club event";
    const canonical = `${origin}/events/${event.slug || slug}`;
    document.title = `${title} · Nail Viet Club`;
    const setMeta = (selector, attrName, attrValue, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta('meta[name="description"]',           "name",     "description", description);
    setMeta('meta[property="og:title"]',          "property", "og:title", title);
    setMeta('meta[property="og:description"]',    "property", "og:description", description);
    setMeta('meta[property="og:image"]',          "property", "og:image", ogImage);
    setMeta('meta[property="og:url"]',            "property", "og:url", canonical);
    setMeta('meta[property="og:type"]',           "property", "og:type", "event");
    setMeta('meta[name="twitter:card"]',          "name",     "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]',         "name",     "twitter:title", title);
    setMeta('meta[name="twitter:description"]',   "name",     "twitter:description", description);
    setMeta('meta[name="twitter:image"]',         "name",     "twitter:image", ogImage);
    let canonEl = document.head.querySelector('link[rel="canonical"]');
    if (!canonEl) {
      canonEl = document.createElement("link");
      canonEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonEl);
    }
    canonEl.setAttribute("href", canonical);
  }, [event, slug]);

  const heroImg = event?.hero_image_url || DEFAULT_HERO;
  const primary = event?.primary_color || "#0E5B4F";
  const accent = event?.secondary_color || "#C9A14A";

  const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Countdown timers for OTP UX
  useEffect(() => {
    if (otpStep !== "otp" || otpExpiresIn <= 0) return;
    const id = setInterval(() => setOtpExpiresIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [otpStep, otpExpiresIn]);
  useEffect(() => {
    if (otpResendIn <= 0) return;
    const id = setInterval(() => setOtpResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [otpResendIn]);

  const validateForm = () => {
    if (!form.business_name.trim() || !form.phone.trim()) { setFormError(t("landing.form_error_required")); return false; }
    const digits = (form.phone.match(/\d/g) || []).join("");
    if (digits.length !== 10 && digits.length !== 11) { setFormError(t("landing.form_error_phone")); return false; }
    return true;
  };

  const requestOtp = async (resend = false) => {
    setFormError("");
    if (!resend && !validateForm()) return;
    setOtpRequesting(true);
    try {
      const r = await api.post(`/nail-events/${slug}/register/request-otp`, {
        phone: form.phone.trim(),
        preferred_language: lang,
      });
      if (r.data.otp_skipped) {
        // SignalWire not configured (dev) — go straight to registration.
        await completeRegistration(null);
        return;
      }
      if (r.data.status === "failed") {
        // SMS provider failed → keep the form open with retry countdown
        setOtpStep("otp");
        setOtpExpiresIn(0);
        setOtpResendIn(r.data.retry_after || 90);
        setFormError(r.data.detail || (lang === "en"
          ? "Could not send code. Please try again in a moment."
          : "Không gửi được mã. Vui lòng thử lại sau giây lát."));
        return;
      }
      setOtpStep("otp");
      setOtpExpiresIn(r.data.expires_in || 300);
      setOtpResendIn(r.data.retry_after || 45);
      setOtpCode("");
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 429 && data?.detail && typeof data.detail === "object") {
        setOtpStep("otp");
        setOtpResendIn(data.detail.retry_after || 45);
        setFormError(data.detail.message || (lang === "en" ? "Please wait before retrying." : "Vui lòng đợi trước khi thử lại."));
      } else {
        setFormError(data?.detail || t("landing.form_error_generic"));
      }
    } finally {
      setOtpRequesting(false);
    }
  };

  const completeRegistration = async (codeOverride) => {
    setSubmitting(true);
    setFormError("");
    try {
      const payload = {
        business_name: form.business_name.trim(),
        phone: form.phone.trim(),
        otp_code: codeOverride !== null ? codeOverride : (otpCode.trim() || null),
        owner_name: form.owner_name.trim() || null,
        email: form.email.trim() || null,
        guest_count: form.guest_count ? parseInt(form.guest_count, 10) : null,
        preferred_language: lang,
        ...track,
      };
      const r = await api.post(`/nail-events/${slug}/register`, payload);
      setSuccessState({
        already: r.data.already_registered,
        smsStatus: r.data.sms_status,
        emailStatus: r.data.email_status,
        registrationId: r.data.registration?.id,
      });
      setOtpStep("form");
      setOtpCode("");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setFormError(err?.response?.data?.detail || t("landing.form_error_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otpStep === "form") {
      await requestOtp();
    } else {
      if (!otpCode.trim() || otpCode.trim().length < 4) {
        setFormError(lang === "en" ? "Please enter the code we sent you." : "Vui lòng nhập mã chúng tôi đã gửi.");
        return;
      }
      await completeRegistration(null);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-8">
        <div className="text-center">
          <h1 className="font-serif text-5xl text-charcoal mb-2">404</h1>
          <p className="text-warmgray-700">{loadError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-charcoal" data-testid="landing-page" style={{ ["--brand-primary"]: primary, ["--brand-accent"]: accent }}>
      {/* HEADER */}
      <Header lang={lang} />

      {/* HERO */}
      <section className="relative overflow-hidden dark-surface">
        <div className="absolute inset-0">
          <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-charcoal-900/60" />
          <div className="absolute inset-0 summit-gradient opacity-90" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
          {successState ? (
            <div className="max-w-3xl mx-auto space-y-6">
              <SuccessCard already={successState.already} smsStatus={successState.smsStatus} emailStatus={successState.emailStatus}
                onReset={() => { setSuccessState(null); setForm({ business_name: "", phone: "", owner_name: "", email: "", guest_count: "" }); }} />
              {successState.registrationId && event?.effective_license_requirement !== "not_required" && (
                <LicenseVerificationCard
                  slug={slug}
                  registrationId={successState.registrationId}
                  primary={primary}
                  accent={accent}
                  licenseRequirement={event?.effective_license_requirement || "optional_ai"}
                />
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-12 gap-10 items-start">
              {/* LEFT — HERO COPY */}
              <div className="md:col-span-7 text-white animate-fade-up">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold-500/40 bg-white/5 backdrop-blur-md">
                  <Star className="w-3.5 h-3.5 text-gold-500" />
                  <span className="text-[11px] uppercase tracking-[0.28em] text-gold-300 font-semibold">{event?.subtitle || t("landing.eyebrow")}</span>
                </div>
                <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight">
                  {event?.name ? (
                    (() => {
                      const parts = event.name.split(/\s+[—–-]\s+/);
                      if (parts.length >= 2) {
                        return (
                          <>
                            {parts.slice(0, -1).join(" — ")}{" "}
                            <span className="italic" style={{ color: accent }}>{parts[parts.length - 1]}</span>
                          </>
                        );
                      }
                      return event.name;
                    })()
                  ) : (
                    <>
                      {t("landing.hero_title_lead")}{" "}
                      <span className="italic" style={{ color: accent }}>{t("landing.hero_title_highlight")}</span>
                    </>
                  )}
                </h1>
                <p className="mt-7 text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">{event?.description || t("landing.hero_subtitle")}</p>

                {event && (
                  <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
                    <InfoTile dark icon={CalendarDays} label={t("landing.event_date_label")} value={event.event_date} />
                    {event.event_time && <InfoTile dark icon={Clock} label={t("landing.event_time_label")} value={event.event_time} />}
                    <InfoTile dark icon={MapPin} label={t("landing.event_location_label")} value={event.venue_name || event.location} />
                  </div>
                )}

                {event?.enable_countdown && countdown && !countdown.done && <Countdown countdown={countdown} lang={lang} dark />}

                <div className="mt-10 flex flex-wrap gap-3">
                  <a href="#signup" data-testid="hero-cta-primary"
                    className="inline-flex items-center gap-2 text-white rounded-full px-8 py-4 font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-12px_rgba(14,91,79,0.6)]"
                    style={{ background: primary }}>
                    {t("landing.cta_primary")} <ArrowRight className="w-4 h-4" />
                  </a>
                  <a href="#agenda" data-testid="hero-cta-secondary"
                    className="inline-flex items-center gap-2 text-white rounded-full px-8 py-4 font-medium border border-white/30 hover:bg-white/10 transition-all duration-300">
                    {t("landing.cta_secondary")}
                  </a>
                </div>
              </div>

              {/* RIGHT — SIGNUP CARD */}
              <SignupCard id="signup" form={form} onChange={handleChange} onSubmit={handleSubmit} submitting={submitting} formError={formError} primary={primary} accent={accent}
                otpStep={otpStep} otpCode={otpCode} setOtpCode={setOtpCode}
                otpExpiresIn={otpExpiresIn} otpResendIn={otpResendIn} otpRequesting={otpRequesting}
                onResendOtp={requestOtp}
                onOtpComplete={(code) => completeRegistration(code)}
                onBackToForm={() => { setOtpStep("form"); setFormError(""); setOtpCode(""); }} />
            </div>
          )}
        </div>

        {/* STATS STRIP */}
        <div className="relative">
          <div className="gold-divider" />
          <div className="bg-charcoal-900/80 backdrop-blur-md">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 dark-surface">
              {(Array.isArray(statsStrip) ? statsStrip : []).map((s, i) => (
                <div key={i} className="text-center md:text-left" data-testid={`stat-strip-${i}`}>
                  <div className="font-serif text-3xl md:text-4xl text-white">{s.value}</div>
                  <div className="text-[10px] uppercase tracking-[0.24em] mt-1 font-semibold text-gold-300">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="gold-divider" />
        </div>
      </section>

      {/* WHY ATTEND */}
      <section className="bg-warmgray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <p className="text-[11px] uppercase tracking-[0.32em] font-semibold mb-5" style={{ color: primary }}>{t("landing.why_eyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal max-w-3xl leading-tight">{t("landing.why_title")}</h2>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(Array.isArray(benefits) ? benefits : []).map((b, i) => {
              const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
              return (
                <div key={i} data-testid={`benefit-card-${i}`}
                  className="group bg-white border border-warmgray-200 rounded-3xl p-7 shadow-[0_8px_30px_-12px_rgba(31,31,31,0.08)] hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(14,91,79,0.25)] hover:border-emerald2/30 transition-all duration-500">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5 transition-colors duration-300"
                    style={{ background: primary, color: "#fff" }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-serif text-2xl text-charcoal mb-2">{b.title}</h3>
                  <p className="text-warmgray-700 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AGENDA */}
      {agenda.length > 0 && (
        <section id="agenda" className="bg-white">
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-24 md:py-32">
            <p className="text-[11px] uppercase tracking-[0.32em] font-semibold mb-5" style={{ color: primary }}>{t("landing.agenda_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal">{t("landing.agenda_title")}</h2>
            <div className="mt-14 relative">
              <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-emerald2 via-gold-500 to-transparent" />
              {agenda.map((item, i) => (
                <div key={i} className="relative pl-10 pb-10 last:pb-0 grid grid-cols-12 gap-6 items-start" data-testid={`agenda-row-${i}`}>
                  <span className="absolute left-0 top-1 w-4 h-4 rounded-full ring-4 ring-warmgray-100" style={{ background: i === 0 ? "#C9A14A" : primary }} />
                  <div className="col-span-12 md:col-span-3 font-serif text-2xl text-charcoal">{item.time}</div>
                  <div className="col-span-12 md:col-span-9">
                    <h3 className="text-xl font-medium text-charcoal">{item.title}</h3>
                    {item.description && <p className="text-warmgray-700 mt-1">{item.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AI ADVISOR */}
      <section className="bg-charcoal-900 dark-surface relative overflow-hidden">
        <div className="absolute inset-0 summit-gradient opacity-80" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-12 items-center text-white">
          <div className="md:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.32em] text-gold-300 font-semibold mb-5">{t("landing.ai_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight">{t("landing.ai_title")}</h2>
            <p className="mt-4 text-gold-300 text-lg font-medium">{t("landing.ai_subtitle")}</p>
            <p className="mt-6 text-white/80 text-lg leading-relaxed max-w-2xl">{t("landing.ai_body")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button type="button" data-testid="ai-section-open"
                onClick={() => { const btn = document.querySelector('[data-testid="ai-chat-open"]'); if (btn) btn.click(); }}
                className="inline-flex items-center gap-2 bg-gold-500 text-charcoal rounded-full px-6 py-3 font-semibold hover:-translate-y-0.5 hover:bg-gold-400 transition-all">
                <Bot className="w-4 h-4" /> {t("landing.ai_cta")}
              </button>
            </div>
            <div className="mt-8 flex flex-wrap gap-2 text-xs">
              {["Business", "Taxes", "HR", "Marketing", "Operations", "Compliance", "AsterPOS"].map((k) => (
                <span key={k} className="px-3 py-1 rounded-full border border-white/15 text-white/70">{k}</span>
              ))}
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-6 md:p-7 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.6)]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gold-500 text-charcoal flex items-center justify-center"><Sparkles className="w-5 h-5" /></div>
                <div>
                  <div className="font-serif text-xl">AsterPOS AI Business Advisor</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">{lang === "en" ? "Powered by AsterPOS Corp" : "Được cung cấp bởi AsterPOS Corp"}</div>
                </div>
              </div>
              <div className="mt-5 space-y-3 text-sm">
                <ChatBubble side="user">{lang === "en" ? "How do I do payroll legally for my nail techs?" : "Làm sao trả lương cho thợ nail đúng luật?"}</ChatBubble>
                <ChatBubble side="ai">{lang === "en" ? "Great question. Most nail techs should be W-2 employees with withholding, not 1099. Here's why it protects your salon..." : "Câu hỏi rất hay. Đa số thợ nail nên là nhân viên W-2 (có khấu trừ thuế), không phải 1099. Đây là lý do giúp tiệm của bạn an toàn..."}</ChatBubble>
                <ChatBubble side="user">{lang === "en" ? "What licenses do I need to open in Texas?" : "Mở tiệm ở Texas cần giấy phép gì?"}</ChatBubble>
              </div>
              <div className="mt-4 flex items-center gap-2 border-t border-white/10 pt-4">
                <Mic className="w-4 h-4 text-white/50" />
                <MessageSquare className="w-4 h-4 text-white/50" />
                <span className="text-xs text-white/60">{lang === "en" ? "Chat & Voice — Vietnamese / English" : "Trò chuyện & Giọng nói — Tiếng Việt / English"}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* POWERED BY */}
      <section className="bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <p className="text-[11px] uppercase tracking-[0.32em] font-semibold mb-5" style={{ color: primary }}>{t("landing.powered_eyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal">{t("landing.powered_title")}</h2>
          <p className="mt-6 text-warmgray-700 text-lg leading-relaxed max-w-3xl mx-auto">{t("landing.powered_body")}</p>
          <div className="mt-10 inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-charcoal-900 text-white text-sm">
            <Sparkles className="w-4 h-4 text-gold-500" />
            <span className="font-medium tracking-wide">AsterPOS Corp</span>
            <span className="w-px h-4 bg-white/20" />
            <span className="text-xs uppercase tracking-[0.22em] text-gold-300">Nail Industry Platform</span>
          </div>
        </div>
      </section>

      {/* SPEAKERS */}
      {event?.speakers && event.speakers.length > 0 && (
        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
            <p className="text-[11px] uppercase tracking-[0.32em] font-semibold mb-5 text-center" style={{ color: primary }}>{lang === "en" ? "Featured speakers" : "Diễn giả nổi bật"}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal text-center">{lang === "en" ? "Speakers" : "Diễn Giả"}</h2>
            <div className="mt-12 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {event.speakers.map((sp) => (
                <div key={sp.id} data-testid={`speaker-${sp.id}`}
                  className={`bg-white rounded-3xl overflow-hidden border ${sp.is_featured ? "border-gold-500/60 shadow-[0_18px_40px_-20px_rgba(201,161,74,0.4)]" : "border-warmgray-200"}`}>
                  <div className="aspect-[4/5] bg-warmgray-100 overflow-hidden">
                    {sp.photo_url ? <img src={sp.photo_url} alt={sp.full_name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-serif text-4xl text-warmgray-500">{sp.full_name?.[0]}</div>}
                  </div>
                  <div className="p-5">
                    <div className="font-serif text-xl text-charcoal flex items-center gap-1.5">
                      {sp.full_name}
                      {sp.is_featured && <Sparkles className="w-3.5 h-3.5 text-gold-500" />}
                    </div>
                    <div className="text-xs text-warmgray-700 mt-1">{[sp.title, sp.company].filter(Boolean).join(" · ")}</div>
                    {sp.topic && <div className="mt-2 text-sm font-medium" style={{ color: primary }}>{sp.topic}</div>}
                    {sp.speaking_time && <div className="text-[10px] uppercase tracking-[0.18em] text-warmgray-500 font-semibold mt-2">{sp.speaking_time}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SPONSORS */}
      <section className="bg-warmgray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <p className="text-[11px] uppercase tracking-[0.32em] font-semibold mb-5 text-center" style={{ color: primary }}>{t("landing.sponsors_eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-4xl text-charcoal text-center">{t("landing.sponsors_title")}</h2>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-5 items-center">
            {(event?.sponsors && event.sponsors.length > 0
              ? event.sponsors
              : Array.from({ length: 5 }, (_, i) => ({ id: `ph-${i}`, name: "" })))
              .map((sp) => (
                <a key={sp.id} href={sp.website || "#"} target={sp.website ? "_blank" : "_self"} rel="noopener noreferrer"
                  data-testid={`sponsor-${sp.id}`}
                  className="block group aspect-[3/2] bg-white border border-warmgray-200 rounded-2xl flex items-center justify-center p-4 hover:-translate-y-0.5 hover:border-gold-500/40 transition-all">
                  {sp.logo_url ? (
                    <img src={sp.logo_url} alt={sp.name} className="max-h-full max-w-full object-contain opacity-80 group-hover:opacity-100" />
                  ) : sp.name ? (
                    <span className="font-serif text-xl text-charcoal">{sp.name}</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.22em] text-warmgray-500 font-semibold">{t("landing.sponsors_placeholder")}</span>
                  )}
                </a>
              ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-charcoal-900 dark-surface">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid md:grid-cols-3 gap-8 text-white/70">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-gold-500" />
              <span className="font-serif text-xl text-white">{t("landing.footer_assoc")}</span>
            </div>
            <p className="mt-3 text-sm text-white/60">© {new Date().getFullYear()} {t("landing.footer_assoc")}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-gold-300 font-semibold mb-3">{lang === "en" ? "Platform" : "Nền tảng"}</p>
            <div className="text-white font-medium">{t("landing.footer_powered")}</div>
            <p className="mt-2 text-sm text-white/60">{lang === "en" ? "Registration · Check-in · SMS · Email · AI" : "Đăng ký · Check-in · SMS · Email · AI"}</p>
          </div>
          <div className="flex md:justify-end items-start gap-3">
            <a href="mailto:hello@asterpos.com" data-testid="footer-contact" className="inline-flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm text-white hover:bg-white/10 transition-all">
              <Mail className="w-4 h-4" /> {t("landing.footer_contact")}
            </a>
            <LanguageToggle variant="dark" />
          </div>
        </div>
      </footer>

      {/* MOBILE STICKY CTA */}
      {!successState && (
        <a href="#signup" data-testid="mobile-sticky-cta"
          className="md:hidden fixed bottom-4 left-4 right-20 z-40 inline-flex items-center justify-center gap-2 text-white rounded-full py-3.5 font-medium shadow-[0_18px_40px_-12px_rgba(14,91,79,0.6)]"
          style={{ background: primary }}>
          {t("landing.cta_primary")} <ArrowRight className="w-4 h-4" />
        </a>
      )}

      {event?.enable_ai_chat !== false && (
        <AIChat eventSlug={slug} brand={aiSettings?.brand_name || "AsterPOS AI Business Advisor"} poweredBy={aiSettings?.powered_by || "AsterPOS Corp"} />
      )}
    </div>
  );
}

function Header({ lang }) {
  return (
    <header className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-5 md:pt-7 flex items-center justify-between text-white">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full border border-gold-500/40 bg-white/5 backdrop-blur-md flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-gold-500" />
        </div>
        <div className="leading-none">
          <div className="font-serif text-xl md:text-[22px] tracking-tight" data-testid="brand-name">Vietnamese Nail Association</div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-gold-300 mt-1.5 font-semibold">{lang === "en" ? "Powered by AsterPOS Corp" : "Được cung cấp bởi AsterPOS Corp"}</div>
        </div>
      </div>
      <LanguageToggle variant="dark" />
    </header>
  );
}

function InfoTile({ icon: Icon, label, value, dark }) {
  if (dark) {
    return (
      <div className="bg-white/5 backdrop-blur-md border border-white/15 rounded-2xl px-4 py-3">
        <div className="flex items-center gap-2 text-gold-300 text-[10px] uppercase tracking-[0.22em] font-semibold">
          <Icon className="w-3.5 h-3.5" />{label}
        </div>
        <div className="mt-1 font-medium text-white text-sm md:text-base leading-snug">{value}</div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-warmgray-200 rounded-2xl px-4 py-3 shadow-sm">
      <div className="flex items-center gap-2 text-warmgray-500 text-[10px] uppercase tracking-[0.22em] font-semibold">
        <Icon className="w-3.5 h-3.5" />{label}
      </div>
      <div className="mt-1 font-medium text-charcoal text-sm md:text-base leading-snug">{value}</div>
    </div>
  );
}

function Countdown({ countdown, lang, dark }) {
  const labels = lang === "en"
    ? { d: "Days", h: "Hours", m: "Min", s: "Sec" }
    : { d: "Ngày", h: "Giờ", m: "Phút", s: "Giây" };
  return (
    <div className="mt-8 grid grid-cols-4 gap-2 max-w-md" data-testid="countdown">
      {["d", "h", "m", "s"].map((k) => (
        <div key={k} className={dark ? "bg-white/5 backdrop-blur-md border border-white/15 rounded-2xl px-3 py-3 text-center text-white" : "bg-white border border-warmgray-200 rounded-2xl px-3 py-3 text-center"}>
          <div className={`font-serif text-3xl leading-none ${dark ? "text-white" : "text-charcoal"}`}>{String(countdown[k]).padStart(2, "0")}</div>
          <div className={`text-[10px] uppercase tracking-[0.22em] mt-2 font-semibold ${dark ? "text-gold-300" : "text-warmgray-500"}`}>{labels[k]}</div>
        </div>
      ))}
    </div>
  );
}

function ChatBubble({ side, children }) {
  if (side === "user") {
    return (
      <div className="flex justify-end">
        <div className="bg-emerald2 text-white rounded-2xl px-4 py-2 max-w-[85%]">{children}</div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="bg-white/10 border border-white/15 text-white rounded-2xl px-4 py-2 max-w-[85%]">{children}</div>
    </div>
  );
}

function SignupCard({ id, form, onChange, onSubmit, submitting, formError, primary, accent,
                      otpStep, otpCode, setOtpCode, otpExpiresIn, otpResendIn, otpRequesting, onResendOtp, onOtpComplete, onBackToForm }) {
  const { t, lang } = useI18n();
  const fmt = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,"0")}`;
  return (
    <div id={id} className="md:col-span-5 animate-fade-up [animation-delay:120ms]">
      <div className="bg-white rounded-3xl p-7 md:p-9 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] border border-white/40 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${primary}, ${accent}, ${primary})` }} />
        <p className="text-[11px] uppercase tracking-[0.28em] font-semibold" style={{ color: primary }}>{t("landing.form_eyebrow")}</p>
        <h3 className="font-serif text-3xl md:text-4xl text-charcoal mt-2">{t("landing.form_title")}</h3>
        <p className="text-warmgray-700 mt-2 text-sm">{t("landing.form_subtitle")}</p>

        {/* Step indicator */}
        <div className="mt-5 flex items-center gap-2" data-testid="registration-step-indicator">
          <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-semibold ${otpStep === "form" ? "text-white" : "bg-emerald2/15 text-emerald2"}`}
                style={otpStep === "form" ? { background: primary } : {}}>1</span>
          <span className={`flex-1 h-px ${otpStep === "otp" ? "bg-emerald2/60" : "bg-warmgray-200"}`} />
          <span className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-semibold ${otpStep === "otp" ? "text-white" : "bg-warmgray-100 text-warmgray-500"}`}
                style={otpStep === "otp" ? { background: primary } : {}}>2</span>
        </div>
        <div className="mt-2 flex justify-between text-[10px] uppercase tracking-[0.22em] text-warmgray-500 font-semibold">
          <span>{lang === "en" ? "Your details" : "Thông tin"}</span>
          <span>{lang === "en" ? "Verify phone" : "Xác minh SĐT"}</span>
        </div>

        <form className="mt-6 space-y-4" onSubmit={onSubmit}>
          {otpStep === "form" ? (
            <>
              <Field
                label={t("landing.form_business_name")}
                required
                testId="registration-input-business"
                placeholder={t("landing.form_business_name_ph")}
                hint={t("landing.form_business_name_hint")}
                value={form.business_name}
                onChange={onChange("business_name")}
                primary={primary}
              />
              <Field label={t("landing.form_phone")} required type="tel" testId="registration-input-phone" placeholder={t("landing.form_phone_ph")} value={form.phone} onChange={onChange("phone")} primary={primary} />
              <Field label="Email" type="email" testId="registration-input-email" placeholder="you@example.com" value={form.email} onChange={onChange("email")} primary={primary} />
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label={t("landing.form_owner_name")}
                  testId="registration-input-owner"
                  placeholder={t("landing.form_owner_name_ph")}
                  hint={t("landing.form_owner_name_hint")}
                  value={form.owner_name}
                  onChange={onChange("owner_name")}
                  primary={primary}
                />
                <Field label={t("landing.form_guest_count")} type="number" testId="registration-input-guests" placeholder={t("landing.form_guest_count_ph")} value={form.guest_count} onChange={onChange("guest_count")} primary={primary} />
              </div>
              {looksSwapped(form.business_name, form.owner_name) && (
                <div data-testid="registration-swap-hint" className="rounded-2xl bg-amber-50 border border-amber-300 px-4 py-3 text-sm text-amber-900">
                  {t("landing.form_swap_hint")}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4" data-testid="registration-otp-step">
              <div className="rounded-2xl bg-emerald2/5 border border-emerald2/20 px-4 py-3 text-sm text-charcoal">
                {lang === "en"
                  ? <>We just sent a 6-digit code to <strong>{form.phone}</strong>.</>
                  : <>Chúng tôi vừa gửi mã 6 số tới <strong>{form.phone}</strong>.</>}
                <button type="button" data-testid="registration-otp-edit-phone" onClick={onBackToForm} className="ml-1 underline text-emerald2 hover:text-espresso text-xs">
                  {lang === "en" ? "Edit" : "Sửa"}
                </button>
              </div>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-2">
                  {lang === "en" ? "6-digit code" : "Mã 6 số"} <span style={{ color: primary }}>*</span>
                </span>
                <div className="w-full flex justify-center py-2">
                  <OtpInput
                    length={6}
                    value={otpCode}
                    onChange={setOtpCode}
                    onComplete={onOtpComplete}
                    loading={submitting || otpRequesting}
                    error={!!formError && otpCode.length === 6}
                    testIdPrefix="registration-otp-input"
                    ariaLabel={id + "-otp"}
                  />
                </div>
              </label>
              <div className="flex items-center justify-between text-xs text-warmgray-700" data-testid="registration-otp-timers">
                <span>
                  {otpExpiresIn > 0
                    ? (lang === "en" ? "Code expires in " : "Mã hết hạn trong ") + fmt(otpExpiresIn)
                    : (lang === "en" ? "Code expired — request a new one." : "Mã đã hết hạn — vui lòng yêu cầu mã mới.")}
                </span>
                <button
                  type="button"
                  data-testid="registration-otp-resend"
                  disabled={otpResendIn > 0 || otpRequesting}
                  onClick={() => onResendOtp(true)}
                  className="text-emerald2 underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                >
                  {otpResendIn > 0
                    ? (lang === "en" ? `Resend in ${otpResendIn}s` : `Gửi lại sau ${otpResendIn}s`)
                    : (lang === "en" ? "Resend code" : "Gửi lại mã")}
                </button>
              </div>
            </div>
          )}

          {formError && <div data-testid="registration-form-error" className="text-sm rounded-xl border border-[#7A2E2E]/20 bg-[#F5DADA] text-[#7A2E2E] px-4 py-3">{formError}</div>}
          <button type="submit" disabled={submitting || otpRequesting} data-testid="registration-form-submit"
            className="w-full text-white rounded-full px-6 py-4 font-medium tracking-wide hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 transition-all duration-300 flex items-center justify-center gap-2 shadow-[0_18px_40px_-12px_rgba(14,91,79,0.4)]"
            style={{ background: primary }}>
            {submitting
              ? t("landing.form_submitting")
              : otpRequesting
                ? (lang === "en" ? "Sending code…" : "Đang gửi mã…")
                : otpStep === "form"
                  ? (lang === "en" ? "Send verification code" : "Gửi mã xác minh")
                  : (lang === "en" ? "Confirm & register" : "Xác minh & đăng ký")}
            {!submitting && !otpRequesting && <Send className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, testId, primary, hint, ...props }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-2">
        {label} {required && <span style={{ color: primary }}>*</span>}
      </span>
      <input data-testid={testId} {...props}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-4 py-3 text-charcoal placeholder:text-warmgray-500/70 focus:outline-none focus:ring-2 focus:ring-emerald2/40 focus:border-emerald2 transition-all" />
      {hint && (
        <span data-testid={`${testId}-hint`} className="mt-1.5 block text-[11px] text-warmgray-600 leading-snug">{hint}</span>
      )}
    </label>
  );
}

// Jul 2026 — soft swap-hint: if business_name looks like a person AND owner_name
// looks like a business, the two fields were likely reversed. Non-blocking:
// the member can ignore the hint and submit anyway.
const BUSINESS_TOKENS = /\b(nails?|spa|salon|studio|beauty|lounge|bar|hair|inc|llc|corp|tiệm|thẩm mỹ)\b/i;
function looksLikePerson(s) {
  const v = (s || "").trim();
  if (!v || BUSINESS_TOKENS.test(v)) return false;
  // 2–4 whitespace-separated tokens, no digits, no & / symbols.
  if (/[0-9&/@#]/.test(v)) return false;
  const parts = v.split(/\s+/);
  return parts.length >= 2 && parts.length <= 4;
}
function looksSwapped(business, owner) {
  const b = (business || "").trim();
  const o = (owner || "").trim();
  if (!b || !o) return false;
  return looksLikePerson(b) && BUSINESS_TOKENS.test(o);
}

function SuccessCard({ already, smsStatus, emailStatus, onReset }) {
  const { t } = useI18n();
  const title = already ? t("landing.already_registered_title") : t("landing.success_title");
  const sent = smsStatus === "sent" || emailStatus === "sent";
  const subtitle = already
    ? t("landing.already_registered_subtitle")
    : sent ? t("landing.success_subtitle") : t("landing.success_subtitle_no_sms");
  return (
    <div className="max-w-2xl mx-auto bg-white rounded-3xl p-10 md:p-14 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] text-center animate-fade-up" data-testid="registration-success">
      <div className="w-16 h-16 rounded-full bg-emerald2/10 text-emerald2 mx-auto flex items-center justify-center mb-6">
        <Sparkles className="w-7 h-7" />
      </div>
      <h2 className="font-serif text-4xl md:text-5xl text-charcoal">{title}</h2>
      <p className="mt-4 text-warmgray-700 text-lg leading-relaxed">{subtitle}</p>
      <button type="button" data-testid="register-another" onClick={onReset}
        className="mt-8 inline-flex items-center gap-2 border border-charcoal/30 text-charcoal rounded-full px-6 py-3 font-medium hover:bg-warmgray-100 transition-all">
        {t("landing.back_to_event")}
      </button>
    </div>
  );
}
